import React, { useState } from "react";
import airports from "../data/airports.json";
import airlinesData from "../data/airlines.json";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, ChevronDown, MapPin, Search, X, Users, DollarSign, Loader2 } from "lucide-react";

function AirportAutocomplete({ label, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [showList, setShowList] = useState(false);

  // Filtrer les aéroports selon la saisie
  const filtered = airports.filter(a =>
    (a.name + " " + a.city + " " + a.iata_code)
      .toLowerCase()
      .includes(query.toLowerCase())
  ).slice(0, 8);

  const handleInput = (e) => {
    setQuery(e.target.value);
    setShowList(true);
    onChange(""); // Réinitialise le code IATA si l'utilisateur modifie
  };

  const handleSelect = (airport) => {
    setQuery(`${airport.city} - ${airport.name} (${airport.iata_code})`);
    onChange(airport.iata_code);
    setShowList(false);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
        <MapPin className="w-4 h-4 text-blue-600" />
        {label}
      </label>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 150)}
        placeholder={placeholder}
        className="mt-1 w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
        autoComplete="off"
      />
      <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <AnimatePresence>
        {showList && filtered.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="absolute z-10 bg-white border border-slate-200 rounded-xl shadow-xl mt-1.5 w-full max-h-64 overflow-auto"
          >
            {filtered.map((a) => (
              <li
                key={a.iata_code}
                onMouseDown={() => handleSelect(a)}
                className="px-4 py-2.5 cursor-pointer hover:bg-blue-50 border-b border-slate-100 last:border-0 flex items-center gap-2"
              >
                <span className="font-bold text-blue-700">{a.iata_code}</span>
                <span>{a.city} - {a.name}</span>
                <span className="ml-auto text-xs text-gray-400 uppercase">{a.country}</span>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

const FlightSearchForm = ({ onSearch }) => {
  const [form, setForm] = useState({
    tripType: "roundTrip",
    originLocationCode: "",
    destinationLocationCode: "",
    departureDate: "",
    returnDate: "",
    adults: 1,
    children: 0,
    infants: 0,
    travelClass: "ECONOMY",
    nonStop: false,
    currencyCode: "XOF",
    includedAirlineCodes: [], // Nouvelles compagnies sélectionnées
  });

  const [multiCityLegs, setMultiCityLegs] = useState([
    { originLocationCode: "", destinationLocationCode: "", departureDate: "" },
    { originLocationCode: "", destinationLocationCode: "", departureDate: "" },
  ]);

  const [showForm, setShowForm] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [showAirlines, setShowAirlines] = useState(false); // Zone compagnies refermée par défaut
  const isRoundTrip = form.tripType === "roundTrip";
  const isMultiCity = form.tripType === "multiCity";

  const handleOpenVoiceAssistant = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-voice-assistant'));
    }
  };

  // Compagnie favorite - Air Côte d'Ivoire
  const favoriteAirline = { code: "HF", name: "Air Côte d'Ivoire" };

  // Compagnies d'Afrique de l'Ouest
  const westAfricanAirlines = [
    { code: "HC", name: "Air Senegal" },
    { code: "2J", name: "Air Burkina" },
    { code: "KP", name: "ASKY Airlines" },
    { code: "L6", name: "Mauritania Airlines" },
    { code: "C2", name: "Ceiba Intercontinental" },
    { code: "QC", name: "Camair-Co" },
  ];

  // Compagnies leaders en Afrique
  const africanLeaderAirlines = [
    { code: "ET", name: "Ethiopian Airlines" },
    { code: "KQ", name: "Kenya Airways" },
    { code: "MS", name: "EgyptAir" },
    { code: "AT", name: "Royal Air Maroc" },
    { code: "AH", name: "Air Algérie" },
    { code: "TU", name: "Tunis Air" },
    { code: "SA", name: "South African Airways" },
    { code: "DT", name: "TAAG Angola Airlines" },
    { code: "ME", name: "Middle East Airlines" },
  ];

  // Compagnies internationales populaires
  const internationalAirlines = airlinesData.data
    .filter(airline => 
      airline.iata_code && 
      airline.iata_code.length > 0 &&
      !westAfricanAirlines.find(a => a.code === airline.iata_code) &&
      !africanLeaderAirlines.find(a => a.code === airline.iata_code) &&
      airline.iata_code !== favoriteAirline.code
    )
    .sort((a, b) => (b.num_aircraft || 0) - (a.num_aircraft || 0))
    .slice(0, 20)
    .map(a => ({ code: a.iata_code, name: a.name }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Gestion de la sélection des compagnies aériennes
  const handleAirlineToggle = (code) => {
    const current = form.includedAirlineCodes;
    if (current.includes(code)) {
      // Retirer la compagnie
      setForm({
        ...form,
        includedAirlineCodes: current.filter(c => c !== code)
      });
    } else {
      // Ajouter la compagnie (max 3)
      if (current.length < 3) {
        setForm({
          ...form,
          includedAirlineCodes: [...current, code]
        });
      }
    }
  };

  const handleTripTypeChange = (tripType) => {
    setForm({
      ...form,
      tripType,
      returnDate: tripType === "roundTrip" ? form.returnDate : "",
    });
  };

  const handleMultiCityLegChange = (index, field, value) => {
    setMultiCityLegs((prev) =>
      prev.map((leg, i) => (i === index ? { ...leg, [field]: value } : leg))
    );
  };

  const addMultiCityLeg = () => {
    setMultiCityLegs((prev) => [
      ...prev,
      { originLocationCode: "", destinationLocationCode: "", departureDate: "" },
    ]);
  };

  const removeMultiCityLeg = (index) => {
    setMultiCityLegs((prev) => prev.filter((_, i) => i !== index));
  };

  // Pour les autocompleteurs
  const handleOrigin = (iata) => setForm({ ...form, originLocationCode: iata });
  const handleDestination = (iata) => setForm({ ...form, destinationLocationCode: iata });

  const handleSubmit = (e) => {
    e.preventDefault();
    const totalPassengers =
      Number(form.adults || 0) + Number(form.children || 0) + Number(form.infants || 0);
    if (totalPassengers < 1) {
      alert("Merci d'ajouter au moins un passager.");
      return;
    }

    if (isMultiCity) {
      const validLegs = multiCityLegs.filter((leg) =>
        leg.originLocationCode && leg.destinationLocationCode && leg.departureDate
      );

      if (validLegs.length < 2) {
        alert("Merci de renseigner au moins deux trajets complets pour un voyage multi-villes.");
        return;
      }
    } else if (!form.originLocationCode || !form.destinationLocationCode || !form.departureDate) {
      alert("Merci de renseigner l'origine, la destination et la date de depart.");
      return;
    }

    if (isRoundTrip && !form.returnDate) {
      alert("Merci de renseigner la date de retour.");
      return;
    }

    if (isRoundTrip && form.returnDate < form.departureDate) {
      alert("La date de retour doit etre apres la date de depart.");
      return;
    }

    setReduced(true);
    
    // Préparer les données pour l'envoi
    const searchData = {
      ...form,
      // Convertir le tableau de codes en chaîne CSV pour l'API Amadeus
      returnDate: isRoundTrip ? form.returnDate : "",
      multiCityItineraries: isMultiCity
        ? JSON.stringify(multiCityLegs.filter((leg) =>
            leg.originLocationCode && leg.destinationLocationCode && leg.departureDate
          ))
        : undefined,
      includedAirlineCodes: form.includedAirlineCodes.length > 0 
        ? form.includedAirlineCodes.join(',') 
        : undefined
    };
    
    onSearch(searchData);
    setTimeout(() => {
      setReduced(false);
      setShowForm(false);
    }, 3000);
  };

  const handleFlyerClick = () => {
    setShowForm(true);
    setReduced(false);
  };

  const handleClose = () => {
    setShowForm(false);
    setReduced(false);
  };

  return (
    <>
      {/* Bouton flottant */}
      {!showForm && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-30">
          <motion.button
            onClick={handleFlyerClick}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-2xl px-6 py-3 text-base sm:text-lg font-bold flex items-center gap-2 shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-200"
          >
            <Search className="w-5 h-5" /> Rechercher un vol
          </motion.button>

          <motion.button
            type="button"
            onClick={handleOpenVoiceAssistant}
            className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white rounded-2xl px-4 sm:px-5 py-3 text-base sm:text-lg font-semibold flex items-center gap-2 shadow-lg"
          >
            <span className="text-lg">🎤</span>
            <span className="hidden sm:inline">Parler à l’IA</span>
          </motion.button>
        </div>
      )}

      {/* Overlay + Form */}
      <AnimatePresence>
        {showForm && (
          <>
            {/* Overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={handleClose}
              className="fixed inset-0 bg-black/30 z-40"
            />

            {/* Formulaire centré */}
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              {!reduced ? (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  transition={{ type: "spring", damping: 20 }}
                  className="w-11/12 max-w-lg bg-white rounded-3xl shadow-2xl ring-1 ring-slate-100 p-6 sm:p-8 flex flex-col gap-4 max-h-[80vh] overflow-y-auto relative"
                >
                  {/* Bouton fermer */}
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-red-100 text-gray-500 hover:text-red-500 flex items-center justify-center transition"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {/* En-tête */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-200">
                      <Plane className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Rechercher un vol</h3>
                      <p className="text-xs text-gray-500">Trouvez les meilleures offres de vols</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-slate-100 p-1.5 border border-slate-200">
                    {[
                      { value: "roundTrip", label: "Aller-retour" },
                      { value: "oneWay", label: "Aller simple" },
                      { value: "multiCity", label: "Multi-villes" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleTripTypeChange(option.value)}
                        className={`rounded-xl px-2 py-2.5 text-sm font-semibold transition-all duration-200 ${
                          form.tripType === option.value
                            ? "bg-white text-blue-600 shadow-md"
                            : "text-gray-500 hover:text-blue-600 hover:bg-white/60"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {!isMultiCity && (
                    <>
                  {/* Origine autocomplete */}
                  <AirportAutocomplete
                    label="Origine"
                    value={form.originLocationCode}
                    onChange={handleOrigin}
                    placeholder="Ville, aéroport ou code IATA"
                  />

                  {/* Destination autocomplete */}
                  <AirportAutocomplete
                    label="Destination"
                    value={form.destinationLocationCode}
                    onChange={handleDestination}
                    placeholder="Ville, aéroport ou code IATA"
                  />

                  {/* Dates */}
                  <div className={isRoundTrip ? "grid grid-cols-2 gap-3" : ""}>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date de départ</label>
                      <input
                        type="date"
                        name="departureDate"
                        value={form.departureDate}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    {isRoundTrip && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date de retour</label>
                      <input
                        type="date"
                        name="returnDate"
                        value={form.returnDate}
                        onChange={handleChange}
                        min={form.departureDate || undefined}
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    )}
                  </div>

                    </>
                  )}

                  {isMultiCity && (
                    <div className="space-y-3">
                      {multiCityLegs.map((leg, index) => (
                        <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3 shadow-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-blue-600 flex items-center gap-1.5">
                              <Plane className="w-4 h-4" />
                              Trajet {index + 1}
                            </span>
                            {multiCityLegs.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeMultiCityLeg(index)}
                                className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                              >
                                <X className="w-3.5 h-3.5" /> Retirer
                              </button>
                            )}
                          </div>
                          <AirportAutocomplete
                            label="Origine"
                            value={leg.originLocationCode}
                            onChange={(iata) => handleMultiCityLegChange(index, "originLocationCode", iata)}
                            placeholder="Ville, aéroport ou code IATA"
                          />
                          <AirportAutocomplete
                            label="Destination"
                            value={leg.destinationLocationCode}
                            onChange={(iata) => handleMultiCityLegChange(index, "destinationLocationCode", iata)}
                            placeholder="Ville, aéroport ou code IATA"
                          />
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date de départ</label>
                            <input
                              type="date"
                              value={leg.departureDate}
                              onChange={(e) => handleMultiCityLegChange(index, "departureDate", e.target.value)}
                              required={isMultiCity}
                              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addMultiCityLeg}
                        className="w-full rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/60 hover:bg-blue-100 px-3 py-2.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition flex items-center justify-center gap-1.5"
                      >
                        <span className="text-base leading-none">+</span> Ajouter un trajet
                      </button>
                    </div>
                  )}

                  {/* Passagers */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-blue-600" /> Passagers
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Adultes</label>
                        <input
                          type="number"
                          name="adults"
                          value={form.adults}
                          onChange={handleChange}
                          min="0"
                          required
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm text-center transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Enfants</label>
                        <input
                          type="number"
                          name="children"
                          value={form.children}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm text-center transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Bébés</label>
                        <input
                          type="number"
                          name="infants"
                          value={form.infants}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm text-center transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Classe */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Classe</label>
                    <select
                      name="travelClass"
                      value={form.travelClass}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    >
                      <option value="ECONOMY">Économie</option>
                      <option value="PREMIUM_ECONOMY">Premium Économie</option>
                      <option value="BUSINESS">Business</option>
                      <option value="FIRST">Première</option>
                    </select>
                  </div>

                  {/* Sélection des compagnies aériennes */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowAirlines(prev => !prev)}
                      aria-expanded={showAirlines}
                      className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 shadow-md border-2 ${
                        showAirlines
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-500 text-white hover:from-blue-700 hover:to-indigo-600'
                          : 'bg-white text-gray-800 border-blue-500 hover:bg-blue-50 shadow-blue-100'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Plane className={`w-5 h-5 ${showAirlines ? '' : 'text-blue-600'}`} />
                        <span className="font-bold text-sm">
                          Compagnies aériennes
                          <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${showAirlines ? 'bg-white/20' : 'bg-blue-100 text-blue-600'}`}>
                            max 3
                          </span>
                          {form.includedAirlineCodes.length > 0 && (
                            <span className={`text-sm ml-2 ${showAirlines ? 'text-white' : 'text-blue-600'}`}>
                              ✓ {form.includedAirlineCodes.length} sélectionnée{form.includedAirlineCodes.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      </span>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${showAirlines ? 'rotate-180' : ''}`} />
                    </button>

                    {showAirlines && (
                      <>
                        <div className="max-h-96 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                      
                      {/* Compagnie Favorite - Air Côte d'Ivoire */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-orange-700 mb-2 flex items-center gap-1">
                          ⭐ COMPAGNIE FAVORITE
                        </h3>
                        <button
                          type="button"
                          onClick={() => handleAirlineToggle(favoriteAirline.code)}
                          disabled={
                            !form.includedAirlineCodes.includes(favoriteAirline.code) &&
                            form.includedAirlineCodes.length >= 3
                          }
                          className={`w-full text-left px-4 py-3 rounded-lg text-sm font-semibold transition shadow-md ${
                            form.includedAirlineCodes.includes(favoriteAirline.code)
                              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white scale-105'
                              : form.includedAirlineCodes.length >= 3
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-orange-400 to-orange-500 text-white hover:from-orange-500 hover:to-orange-600'
                          }`}
                        >
                          <span className="font-extrabold text-base">{favoriteAirline.code}</span> - {favoriteAirline.name} ✈️
                        </button>
                      </div>

                      {/* Compagnies d'Afrique de l'Ouest */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-green-700 mb-2">
                          🌍 AFRIQUE DE L'OUEST
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {westAfricanAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-green-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-green-50 border border-green-200'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Compagnies leaders en Afrique */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-amber-700 mb-2">
                          🦁 COMPAGNIES LEADERS AFRICAINES
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {africanLeaderAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-amber-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-amber-50 border border-amber-200'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Compagnies internationales */}
                      <div>
                        <h3 className="text-xs font-bold text-blue-700 mb-2">
                          ✈️ COMPAGNIES INTERNATIONALES
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {internationalAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-blue-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-blue-50 border'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Compagnies sélectionnées */}
                    {form.includedAirlineCodes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {form.includedAirlineCodes.map((code) => {
                          // Chercher dans toutes les catégories
                          let airlineName = code;
                          if (code === favoriteAirline.code) {
                            airlineName = favoriteAirline.name;
                          } else {
                            const found = [...westAfricanAirlines, ...africanLeaderAirlines, ...internationalAirlines]
                              .find(a => a.code === code);
                            if (found) airlineName = found.name;
                          }
                          
                          return (
                            <span
                              key={code}
                              className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {airlineName}
                              <button
                                type="button"
                                onClick={() => handleAirlineToggle(code)}
                                className="ml-1 hover:text-red-600"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  {/* Vol direct */}
                  <label className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:border-blue-200 transition">
                    <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Plane className="w-4 h-4 text-blue-600" /> Vol direct seulement
                    </span>
                    <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.nonStop ? "bg-blue-500" : "bg-gray-300"}`}>
                      <input
                        type="checkbox"
                        name="nonStop"
                        checked={form.nonStop}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${form.nonStop ? "translate-x-5" : "translate-x-0.5"}`} />
                    </span>
                  </label>

                  {/* Devise */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4 text-blue-600" /> Devise
                    </label>
                    <select
                      name="currencyCode"
                      value={form.currencyCode}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-gray-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    >
                      <option value="XOF">XOF</option>
                      {/*  <option value="GNF">GNF</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option> */}
                    </select>
                  </div>

                  {/* Bouton recherche */}
                  <button
                    type="submit"
                    className="mt-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-200 hover:shadow-xl hover:from-blue-700 hover:to-cyan-600 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                  >
                    <Search className="w-5 h-5" /> Rechercher
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="reduced"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  transition={{ type: "spring", damping: 20 }}
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-2xl px-8 py-4 text-lg font-bold flex items-center gap-3 shadow-xl shadow-blue-500/30 relative"
                >
                  <Loader2 className="w-5 h-5 animate-spin" /> Recherche en cours...
                  <button
                    type="button"
                    onClick={handleClose}
                    className="ml-auto w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FlightSearchForm;
// ...existing code...
